import type React from 'react';
import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import * as styles from './styles';
import { SidebarFooter } from './SidebarFooter/SidebarFooter';
import { SidebarGroup } from './SidebarGroup/SidebarGroup';
import { SidebarItem } from './SidebarItem/SidebarItem';
import { SidebarItemDropdown } from './SidebarItemDropdown/SidebarItemDropdown';
import { SidebarItemDropdownEntry } from './SidebarItemDropdownEntry/SidebarItemDropdownEntry';
import SidebarMediaRequestItem from './SidebarMediaRequestItem';
import SidebarOnboarding from './SidebarOnboarding';
import { SidebarUser } from './SidebarUser/SidebarUser';

import { type AccessibleFleet, Location, locationName, type Session } from '@cobliteam/common-types';

import type { WebData } from '@cobliteam/api-lib';

import { useOnboardingStore } from '@cobliteam/onboarding';
import { RoutingPermissions } from '@cobliteam/routing/src/permissions';

import { useHubspot, usePermissions, useUser } from '@cobliteam/hooks';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';

import { MenuItem, MenuItemType } from '@cobliteam/base-components';
import { badgeColors, BadgeSidebar, type BadgeSidebarProps, BadgeSidebarType } from '@cobliteam/base-components/src/BadgeSidebar/Badge';

import Icon from '@cobliteam/ui/icons';

export interface SidebarProps {
  open: boolean;
  userEmail: string;
  currentLocation: Location;
  loginWebData: WebData<Session>;
  onToggleClick: () => void;
  onSidebarItemClick?: (location: Location) => void;
  changeUserFleet: (fleet: AccessibleFleet) => void;
  logout: () => void;
  accessibleFleetsWebData: WebData<AccessibleFleet[]>;
}

const then = new Date();
then.setMonth(then.getMonth() - 1);

export const Sidebar: React.FC<SidebarProps> = ({ currentLocation, loginWebData, ...props }) => {
  const hubspot = useHubspot();
  const history = useHistory();
  const { useGetUserData } = useUser();
  const { data: userData } = useGetUserData();
  const { isAccessAllowed } = usePermissions();
  const hasCreateUpdateDeleteRoutesPermission = isAccessAllowed(RoutingPermissions.CREATE_UPDATE_DELETE_ROUTES);
  const { progressBar } = useOnboardingStore();

  const { showMulticamLandingPage, showAdvancedAnalyticsPage, integrationsPage: showIntegrationsPage, safetyRanking } = useFeatureFlags();

  useEffect(() => {
    hubspot.moveToRight();
  }, [history.location.pathname]);

  const renderSidebarItem = (
    location: Location,
    icon: React.ReactElement<SVGAElement>,
    customOptions?: {
      badgeType?: BadgeSidebarType;
      title?: string;
      featureFlag?: boolean;
    }
  ) => {
    const { badgeType, title, featureFlag } = customOptions || {};

    if (featureFlag === false) return null;

    return (
      <SidebarItem
        location={location}
        sidebarOpen={props.open}
        active={currentLocation.includes(location)}
        icon={icon}
        badge={badgeType && <BadgeSidebar type={badgeType} />}
        dataTest={`sidebar-item-${locationName[location].toLowerCase().replace(/ /g, '-')}`}
      >
        {title || locationName[location]}
      </SidebarItem>
    );
  };

  const renderSidebarDropdownItem = (
    location: Location,
    badge?: BadgeSidebarProps,
    onClick?: () => void,
    featureFlag?: boolean,
    activeLocations?: Location[],
    title?: string,
    active?: boolean
  ) => {
    if (featureFlag === false) return null;
    return (
      <SidebarItemDropdownEntry
        location={location}
        active={active || activeLocations?.includes(currentLocation) || currentLocation === location}
        dataTest={`sidebar-item-${locationName[location].toLowerCase().replace(/ /g, '-')}`}
        onClick={onClick}
      >
        <>
          {title || locationName[location]}
          {badge && !badge.expired && <div className={styles.badgeCircle(badgeColors[badge.type])} />}
        </>
      </SidebarItemDropdownEntry>
    );
  };

  const renderMultiCamPage = () => {
    if (showMulticamLandingPage) {
      return renderSidebarItem(Location.MULTICAM_LANDING_PAGE, <Icon name='video' />, {
        badgeType: userData?.hasCameraFlag ? undefined : BadgeSidebarType.NEW,
        title: 'Cobli Cam',
      });
    }

    return renderSidebarItem(Location.CAMERA_LP, <Icon name='video' />, {
      title: 'Cobli Cam',
    });
  };

  const reportPathsWithParams = [
    Location.REPORTS,
    Location.REPORTS_NEW,
    Location.REPORTS_TRIP_AND_STOP,
    Location.REPORTS_TRIP_AND_STOP_TYPE,
    Location.REPORTS_TRIP_AND_STOP_DETAILS,
    Location.REPORTS_TRIP_AND_STOP_DAILY,
    Location.REPORTS_FLEET_SAFETY_TYPE,
    Location.REPORTS_SAFETY_EVENTS,
  ].flatMap((location) => {
    if (location.includes(':type')) {
      return [location.replace(':type', 'driver'), location.replace(':type', 'vehicle')] as Location[];
    }
    return [location];
  });

  const renderSidebarEntries = () => (
    <div role='tabsEntries' className={styles.sidebarScroll(props.open)}>
      <SidebarGroup>{renderSidebarItem(Location.HOME, <Icon name='house' />)}</SidebarGroup>

      <SidebarGroup>
        {renderSidebarItem(Location.MONITORING, <Icon name='map' />)}
        {renderSidebarItem(Location.TRACKING_HISTORY, <Icon name='route' />)}

        <SidebarItemDropdown
          title='Política de Frota'
          icon={<Icon name='file-text' />}
          active={[
            Location.FLEET_POLICY,
            Location.FLEET_POLICY_GEOFENCE,
            Location.FLEET_POLICY_IDLE_ENGINE,
            Location.FLEET_POLICY_DEVICE_CONNECTION,
            Location.TRIGGERS,
            Location.TRIGGERS_CREATE,
            Location.TRIGGERS_EDIT,
          ].includes(currentLocation)}
          sidebarOpen={props.open}
          groupedLocations={[
            Location.FLEET_POLICY,
            Location.FLEET_POLICY_GEOFENCE,
            Location.FLEET_POLICY_IDLE_ENGINE,
            Location.FLEET_POLICY_DEVICE_CONNECTION,
            Location.TRIGGERS,
            Location.TRIGGERS_CREATE,
            Location.TRIGGERS_EDIT,
          ]}
          badge={<BadgeSidebar type={BadgeSidebarType.NEW} />}
        >
          {renderSidebarDropdownItem(Location.FLEET_POLICY, undefined, undefined, undefined, undefined, 'Página inicial')}
          {renderSidebarDropdownItem(Location.TRIGGERS, undefined, undefined, undefined, undefined, 'Regras da frota')}
          {renderSidebarDropdownItem(Location.TRIGGERS_CREATE, undefined, undefined, undefined, undefined, 'Criar regra')}
        </SidebarItemDropdown>

        {userData?.hasCameraFlag ? (
          <SidebarItemDropdown
            title='Cobli Cam'
            icon={<Icon name='video' />}
            active={
              [
                Location.CAMERA_EVENTS,
                Location.CAMERA_EVENTS_FAVORITES.replace(':view', 'favorites') as Location,
                Location.CAMERA_REQUESTS,
                Location.CAMERA_REQUESTS_FAVORITES.replace(':view', 'favorites') as Location,
                Location.CAMERA_TRIP_IMAGE_HISTORY,
                Location.CAMERA_TRIP_IMAGE_HISTORY_ALL.replace(':view', 'all') as Location,
              ].includes(currentLocation) || currentLocation.includes('/camera')
            }
            sidebarOpen={props.open}
            groupedLocations={[
              Location.CAMERA_EVENTS,
              Location.CAMERA_EVENTS_FAVORITES.replace(':view', 'favorites') as Location,
              Location.CAMERA_REQUESTS,
              Location.CAMERA_REQUESTS_FAVORITES.replace(':view', 'favorites') as Location,
              Location.CAMERA_TRIP_IMAGE_HISTORY,
              Location.CAMERA_TRIP_IMAGE_HISTORY_ALL.replace(':view', 'all') as Location,
            ]}
          >
            {renderSidebarDropdownItem(
              Location.CAMERA_EVENTS,
              undefined,
              undefined,
              undefined,
              [Location.CAMERA_EVENTS, Location.CAMERA_EVENTS_FAVORITES.replace(':view', 'favorites') as Location],
              undefined,
              currentLocation.includes('/camera/events')
            )}
            <SidebarMediaRequestItem currentLocation={currentLocation} />
            {renderSidebarDropdownItem(
              Location.CAMERA_TRIP_IMAGE_HISTORY,
              undefined,
              undefined,
              undefined,
              [Location.CAMERA_TRIP_IMAGE_HISTORY, Location.CAMERA_TRIP_IMAGE_HISTORY_ALL.replace(':view', 'all') as Location],
              undefined,
              currentLocation.includes('/camera/images')
            )}
          </SidebarItemDropdown>
        ) : (
          renderMultiCamPage()
        )}
      </SidebarGroup>

      <SidebarGroup>
        <>
          {hasCreateUpdateDeleteRoutesPermission ? (
            <SidebarItemDropdown
              title='Serviços e Entregas'
              icon={<Icon name='map-pin-check' />}
              active={[Location.SERVICES_AND_DELIVERIES_CONTROL_TOWER, Location.SERVICES_AND_DELIVERIES_PLANNING].includes(currentLocation)}
              sidebarOpen={props.open}
              groupedLocations={[Location.SERVICES_AND_DELIVERIES_PLANNING, Location.SERVICES_AND_DELIVERIES_CONTROL_TOWER]}
              dataCy='services-and-deliveries-sidebar-dropdown'
            >
              {renderSidebarDropdownItem(Location.SERVICES_AND_DELIVERIES_PLANNING)}
              {renderSidebarDropdownItem(Location.SERVICES_AND_DELIVERIES_CONTROL_TOWER)}
            </SidebarItemDropdown>
          ) : (
            renderSidebarItem(Location.SERVICES_AND_DELIVERIES_CONTROL_TOWER, <Icon name='arrow-up-down' />, {
              title: 'Serviços e Entregas',
            })
          )}
        </>
        <SidebarItemDropdown
          title='Documentos'
          icon={<Icon name='files' />}
          active={[Location.PROOF_OF_CONCLUSION, Location.CHECKLIST, Location.CHECKLIST_RECEIVED, Location.CHECKLIST_CREATED].includes(currentLocation)}
          groupedLocations={[Location.PROOF_OF_CONCLUSION, Location.CHECKLIST, Location.CHECKLIST_RECEIVED, Location.CHECKLIST_CREATED]}
          sidebarOpen={props.open}
        >
          {renderSidebarDropdownItem(Location.PROOF_OF_CONCLUSION)}
          {renderSidebarDropdownItem(Location.CHECKLIST, undefined, undefined, undefined, [Location.CHECKLIST, Location.CHECKLIST_RECEIVED, Location.CHECKLIST_CREATED])}
        </SidebarItemDropdown>
      </SidebarGroup>

      <SidebarGroup>
        <SidebarItemDropdown
          title='Segurança'
          icon={<Icon name='shield-alert' />}
          active={[Location.SPEEDING, Location.DRIVER_BEHAVIOR, Location.INSURANCE].includes(currentLocation)}
          sidebarOpen={props.open}
          dataTest='sidebar-item-seguranca'
          groupedLocations={[Location.SPEEDING, Location.DRIVER_BEHAVIOR, Location.SAFETY_RANKING, Location.SAFETY_RANKING_PARAMETERS]}
        >
          {renderSidebarDropdownItem(Location.SPEEDING)}
          {renderSidebarDropdownItem(
            safetyRanking ? Location.SAFETY_RANKING : Location.DRIVER_BEHAVIOR,
            undefined,
            undefined,
            undefined,
            [Location.DRIVER_BEHAVIOR, Location.SAFETY_RANKING, Location.SAFETY_RANKING_PARAMETERS],
            'Modo de condução'
          )}
        </SidebarItemDropdown>
        <SidebarItemDropdown
          title='Análises'
          icon={<Icon name='chart-column-big' />}
          badge={<BadgeSidebar type={BadgeSidebarType.NEW} />}
          active={[Location.PRODUCTIVITY, Location.ADVANCED_ANALYTICS, ...reportPathsWithParams].includes(currentLocation)}
          sidebarOpen={props.open}
          groupedLocations={[Location.PRODUCTIVITY, Location.REPORTS, Location.ADVANCED_ANALYTICS]}
        >
          {renderSidebarDropdownItem(Location.PRODUCTIVITY)}
          {renderSidebarDropdownItem(Location.REPORTS, { type: BadgeSidebarType.NEW }, undefined, undefined, reportPathsWithParams)}
          {renderSidebarDropdownItem(Location.ADVANCED_ANALYTICS, undefined, undefined, showAdvancedAnalyticsPage)}
        </SidebarItemDropdown>

        <SidebarItemDropdown
          title='Gastos'
          icon={<Icon name='dollar-sign' />}
          active={[Location.FUEL_CARD, Location.MAINTENANCE, Location.COSTS].includes(currentLocation)}
          groupedLocations={[Location.FUEL_CARD, Location.MAINTENANCE, Location.COSTS]}
          sidebarOpen={props.open}
        >
          {renderSidebarDropdownItem(Location.FUEL_CARD)}
          {renderSidebarDropdownItem(Location.MAINTENANCE)}
          {renderSidebarDropdownItem(Location.COSTS)}
        </SidebarItemDropdown>
      </SidebarGroup>

      <SidebarGroup>
        <SidebarItemDropdown
          title='Minha frota'
          icon={<Icon name='car-front' />}
          active={[Location.VEHICLES, Location.DEVICES, Location.DRIVER_MANAGEMENT, Location.VEHICLES_GROUPS].includes(currentLocation)}
          sidebarOpen={props.open}
          groupedLocations={[Location.VEHICLES, Location.DEVICES, Location.DRIVER_MANAGEMENT, Location.VEHICLES_GROUPS]}
        >
          {renderSidebarDropdownItem(Location.VEHICLES)}
          {renderSidebarDropdownItem(Location.VEHICLES_GROUPS)}
          {renderSidebarDropdownItem(Location.DEVICES)}
          {renderSidebarDropdownItem(Location.DRIVER_MANAGEMENT)}
        </SidebarItemDropdown>
        {showIntegrationsPage && (
          <SidebarItemDropdown
            title='Integrações'
            icon={<Icon name='workflow' />}
            active={[Location.APPS, Location.APIS, Location.WEBHOOKS].includes(currentLocation)}
            sidebarOpen={props.open}
            dataCy='routes-sidebar-dropdown'
            groupedLocations={[Location.APPS, Location.APIS, Location.WEBHOOKS]}
          >
            {renderSidebarDropdownItem(Location.APPS)}
            {renderSidebarDropdownItem(Location.APIS)}
            {renderSidebarDropdownItem(Location.WEBHOOKS)}
          </SidebarItemDropdown>
        )}
      </SidebarGroup>
    </div>
  );

  return (
    <div className={styles.sidebar(props.open, progressBar)}>
      <div className={styles.menu}>
        <div className={styles.sidebarToggle}>
          {props.open ? (
            <>
              <MenuItem type={MenuItemType.LIGHT}>Painel</MenuItem>
              <div onClick={props.onToggleClick} className='cursor-pointer'>
                <Icon name='menu' />
              </div>
            </>
          ) : (
            <div onClick={props.onToggleClick} className='mx-auto cursor-pointer'>
              <Icon name='chevron-right' />
            </div>
          )}
        </div>
        <SidebarUser sidebarOpen={props.open} currentLocation={currentLocation} changeUserFleet={props.changeUserFleet} />
        <SidebarOnboarding sidebarOpen={props.open} />
        {renderSidebarEntries()}
      </div>

      <SidebarFooter open={props.open} logout={props.logout} />
    </div>
  );
};
